import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useDecideTimesheet, useLabourQueue, type LabourQueueRow } from "../../lib/api";
import { Money, SectionCard, Stat } from "./shared";

/** Timesheet approval (HRX-03): accepting or returning somebody's booked hours.
 *
 * Nothing here existed before. `Employee.timesheet_approver` was unset on all 103 active employees and — unlike
 * leave and expense, where Frappe HR validates the approver on the document — ERPNext never reads it: a
 * Timesheet submits freely and its cost reaches the project the moment it does. So 209 timesheets carrying
 * 8,296 hours and 135.85 M CDF landed on seven projects with no decision recorded against any of them.
 *
 * The decision is therefore a record of its own, made after the cost has landed, which is also the real
 * sequence at the customer: people book time, the cost lands, the manager reviews. Four eyes, as wave J established for
 * contractor time — the button is not offered to the person whose hours these are, nor to whoever keyed the
 * timesheet in, and the server refuses it either way. */
export default function Approvals({ company }: { company: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const [mine, setMine] = useState(1);
  const q = useLabourQueue(company || undefined, mine);
  const decide = useDecideTimesheet();
  const [returning, setReturning] = useState<string | null>(null);
  const [note, setNote] = useState("");

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;

  const submitReturn = (row: LabourQueueRow) => {
    if (!note.trim()) return;
    decide.mutate({ timesheet: row.name, decision: "Returned", note },
      { onSuccess: () => { setReturning(null); setNote(""); } });
  };

  return (
    <div className="mt-4 space-y-4" data-labour-approvals>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-labour-approval-strip>
        <Stat label={t("lab.pending")} value={d.summary.pending} cls={d.summary.pending ? "text-status-amber" : ""} />
        <Stat label={t("lab.unapproved")} value={<Money value={d.summary.pending_cost} lng={lng} />}
          hint={t("lab.hoursN", { n: d.summary.pending_hours })} />
        <Stat label={t("lab.approved")} value={d.summary.approved} cls="text-status-green" />
        <Stat label={t("lab.returned")} value={d.summary.returned} />
        <Stat label={t("lab.noApprover")} value={d.summary.no_approver}
          cls={d.summary.no_approver ? "text-status-red" : ""} hint={t("lab.noApproverHint")} />
      </div>

      {d.is_hr && (
        <div className="flex gap-1 text-xs" data-labour-scope>
          {[1, 0].map((v) => (
            <button key={v} onClick={() => setMine(v)} data-labour-scope-option={v}
              className={`rounded-full border px-3 py-1 font-semibold ${
                mine === v ? "border-brand-primary bg-brand-primary text-white" : "border-brand-grey-light text-brand-grey"}`}>
              {t(v ? "lab.scopeMine" : "lab.scopeAll")}
            </button>
          ))}
        </div>
      )}

      <SectionCard title={t("lab.queue")} aside={t("lab.queueN", { n: d.rows.length })}>
        {d.rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-labour-queue-empty>{t("lab.queueEmpty")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
              <tr>
                <th className="px-4 py-2">{t("wfa.person")}</th>
                <th className="px-4 py-2">{t("lab.week")}</th>
                <th className="px-2 py-2 text-right">{t("lab.hours")}</th>
                <th className="px-2 py-2 text-right">{t("lab.cost")}</th>
                <th className="px-4 py-2">{t("lab.decision")}</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r) => (
                <Fragment key={r.name}>
                  <tr className="border-t border-brand-grey-light" data-labour-queue-row={r.name}>
                    <td className="px-4 py-2">
                      <div className="font-medium">{r.employee_name ?? r.employee}</div>
                      <div className="text-[11px] text-brand-grey">{r.designation}{r.company ? ` · ${r.company}` : ""}</div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="tabular-nums">{r.start_date} → {r.end_date}</div>
                      {r.project && <Link to={`/projects/${r.project}`} className="text-[11px] text-brand-primary hover:underline">{r.project}</Link>}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.hours}</td>
                    <td className="px-2 py-2 text-right font-semibold"><Money value={r.cost} lng={lng} /></td>
                    <td className="px-4 py-2">
                      {r.refusal ? (
                        <span className="text-[11px] text-brand-grey" data-labour-refusal={r.refusal}>{t(`lab.refusal.${r.refusal}`)}</span>
                      ) : (
                        <div className="flex gap-2">
                          <button onClick={() => decide.mutate({ timesheet: r.name, decision: "Approved" })}
                            disabled={decide.isPending} data-labour-approve={r.name}
                            className="rounded bg-status-green px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">
                            {t("lab.approve")}
                          </button>
                          <button onClick={() => { setReturning(returning === r.name ? null : r.name); setNote(""); }}
                            data-labour-return={r.name}
                            className="rounded border border-brand-grey-light px-3 py-1 text-xs font-semibold text-brand-charcoal">
                            {t("lab.return")}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {returning === r.name && (
                    <tr className="bg-[#F6F8FA]" data-labour-return-form={r.name}>
                      <td colSpan={5} className="px-4 py-3">
                        <label className="text-[11px] uppercase tracking-wide text-brand-grey">{t("lab.returnWhy")}</label>
                        <div className="mt-1 flex flex-wrap gap-2">
                          <input value={note} onChange={(e) => setNote(e.target.value)} data-labour-return-note
                            placeholder={t("lab.returnPlaceholder")}
                            className="min-w-[18rem] flex-1 rounded border border-brand-grey-light px-3 py-1.5 text-sm" />
                          <button onClick={() => submitReturn(r)} disabled={!note.trim() || decide.isPending}
                            data-labour-return-confirm
                            className="rounded bg-status-amber px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                            {t("lab.returnConfirm")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      {decide.isError && <ErrorBox error={decide.error} />}

      <SectionCard title={t("lab.decided")} aside={t("lab.decidedN", { n: d.decided.length })}>
        <div className="max-h-[24rem] overflow-y-auto">
          <table className="w-full text-sm">
            <tbody>
              {d.decided.map((r) => (
                <tr key={r.name} className="border-t border-brand-grey-light" data-labour-decided-row={r.name}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{r.employee_name ?? r.employee}</div>
                    <div className="text-[11px] text-brand-grey">{r.start_date} → {r.end_date}</div>
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-brand-grey">{r.hours} h</td>
                  <td className="px-2 py-2 text-right"><Money value={r.cost} lng={lng} /></td>
                  <td className="px-4 py-2">
                    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${
                      r.state === "Approved" ? "bg-green-50 text-status-green" : "bg-amber-50 text-status-amber"}`}>
                      {t(`lab.state.${r.state}`)}
                    </span>
                    <div className="text-[11px] text-brand-grey">{r.decision?.decided_by}</div>
                    {r.decision?.note && <div className="max-w-md text-[11px] italic text-brand-grey">{r.decision.note}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
